// Firebase provider — typing channel data access.
//
// Relocated (S2) from src/features/chat/typing.js, verbatim except every
// Firestore call now uses the lazy `getDb()` instead of a module-level `db`.
//
// Schema (Firestore):
//   typing/{partyCode}/users/{userId} = { handle, expiresAt }
//
// expiresAt is a plain epoch ms timestamp (Date.now() + 4000) — not a Firestore
// serverTimestamp. We don't need server-authoritative ordering here, and a
// client-side numeric is easier to filter on with onSnapshot (no extra fetch
// needed to resolve the timestamp).
//
// All writes are best-effort: typing is purely transient UI state. We never
// surface errors to the user — they'd just see a stale typing pill at worst,
// which clients filter out via expiresAt anyway.
import {
  collection,
  doc,
  onSnapshot,
} from '@firebase/firestore';
import { setDoc, deleteDoc } from 'firebase/firestore';
import { getDb } from './client.js';
import { reportFirestoreError } from '../../firebase-health.js';

// 4-second TTL matches the design spec (auto-stop on 4s idle).
export const TYPING_TTL_MS = 4000;

/**
 * Announce that `userId` is typing in `code`. Refreshes `expiresAt` on every
 * call — composer throttles to 1/sec so we don't hammer Firestore.
 */
export async function startTyping(code, userId, handle) {
  if (!code || !userId) return;
  try {
    await setDoc(doc(getDb(), 'typing', code, 'users', userId), {
      handle: handle || '',
      expiresAt: Date.now() + TYPING_TTL_MS,
    });
  } catch (err) {
    reportFirestoreError(err);
  }
}

/**
 * Stop the typing signal. Best-effort: a missed delete will be filtered out
 * client-side via the expiresAt timestamp.
 */
export async function stopTyping(code, userId) {
  if (!code || !userId) return;
  try {
    await deleteDoc(doc(getDb(), 'typing', code, 'users', userId));
  } catch (err) {
    reportFirestoreError(err);
  }
}

/**
 * Subscribe to typing entries for `code`. Yields a fresh array on every
 * change; entries with `expiresAt < Date.now()` are dropped client-side.
 * Returns the unsubscribe function — caller MUST return it from useEffect.
 */
export function subscribeTyping(code, cb) {
  if (!code) return () => {};
  return onSnapshot(
    collection(getDb(), 'typing', code, 'users'),
    (snapshot) => {
      const now = Date.now();
      const rows = [];
      snapshot.forEach((d) => {
        const data = d.data() || {};
        const expiresAt = typeof data.expiresAt === 'number' ? data.expiresAt : 0;
        if (expiresAt < now) return;
        rows.push({
          id: d.id,
          handle: data.handle || '',
          avatarSeed: d.id,
          expiresAt,
        });
      });
      cb(rows);
    },
    (err) => reportFirestoreError(err),
  );
}
